import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Package,
  Search,
  RotateCcw,
  User,
  Hash,
  TriangleAlert as AlertTriangle,
  CircleCheck as CheckCircle2,
  Loader as Loader2,
  ScanBarcode,
  X,
} from 'lucide-react'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { QuantityStepper } from '../components/ui/QuantityStepper'
import { LoadingSpinner } from '../components/ui/LoadingSpinner'
import { EmptyState } from '../components/inhouse/ui/empty-state'
import { useAuth } from '../contexts/AuthContext'
import { useNotification } from '../contexts/NotificationContext'
import { getAdminId } from '../utils/getAdminId'
import { useReturnShipment } from '../hooks/use-return-shipment'
import type {
  ReturnLineItem,
  ReturnLookupCandidate,
  ReturnLookupResult,
  ShopifyPushSummary,
} from '../types/returns'

function ReturnItemImage({ src, alt }: { src: string | null; alt: string }) {
  const [failed, setFailed] = useState(false)
  const [loaded, setLoaded] = useState(false)

  if (failed || !src) {
    return (
      <div className="w-14 h-14 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center shrink-0">
        <Package className="h-6 w-6 text-slate-400" />
      </div>
    )
  }

  return (
    <div className="relative w-14 h-14 rounded-lg overflow-hidden bg-slate-100 dark:bg-slate-800 shrink-0">
      {!loaded && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Package className="h-5 w-5 text-slate-300 animate-pulse" />
        </div>
      )}
      <img
        src={src}
        alt={alt}
        loading="lazy"
        crossOrigin="anonymous"
        onLoad={() => setLoaded(true)}
        onError={() => setFailed(true)}
        className={`w-full h-full object-contain transition-opacity duration-200 ${loaded ? 'opacity-100' : 'opacity-0'}`}
      />
    </div>
  )
}

function ResolutionBadge({ resolution }: { resolution: ReturnLineItem['resolution'] }) {
  if (resolution === 'sku' || resolution === 'variant') return null
  if (resolution === 'ambiguous') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
        <AlertTriangle className="h-3 w-3" />
        Ambiguous SKU
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200">
      <AlertTriangle className="h-3 w-3" />
      Not mapped
    </span>
  )
}

interface SuccessState {
  returnNumber: string
  itemsCount: number
  totalQuantity: number
  push?: ShopifyPushSummary
}

export function ReturnShipment() {
  const { user } = useAuth()
  const { success, error: showError, info, warning } = useNotification()
  const adminId = useMemo(() => getAdminId(user), [user])

  const {
    loading,
    confirming,
    shopifySync,
    lookup,
    lookupValue,
    selectCandidate,
    clearLookup,
    confirmReturn,
  } = useReturnShipment(adminId)

  const [scanInput, setScanInput] = useState('')
  const [quantities, setQuantities] = useState<Record<string, number>>({})
  const [notes, setNotes] = useState('')
  const [successState, setSuccessState] = useState<SuccessState | null>(null)

  const inputRef = useRef<HTMLInputElement>(null)
  const scanBufferRef = useRef('')
  const scanTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastScanTimeRef = useRef(0)
  const lastScanValueRef = useRef('')
  const handleLookupRef = useRef<(value: string) => void>(() => undefined)

  const selectableLines = useMemo(
    () => (lookup?.lines || []).filter((l) => l.resolution === 'sku' || l.resolution === 'variant'),
    [lookup]
  )

  const blockedLines = useMemo(
    () => (lookup?.lines || []).filter((l) => l.resolution === 'unresolved' || l.resolution === 'ambiguous'),
    [lookup]
  )

  const selectedTotal = useMemo(() => {
    return selectableLines.reduce((sum, line) => sum + (quantities[line.lineKey] || 0), 0)
  }, [selectableLines, quantities])

  const selectedCount = useMemo(() => {
    return selectableLines.filter((line) => (quantities[line.lineKey] || 0) > 0).length
  }, [selectableLines, quantities])

  const applyResult = useCallback((result: ReturnLookupResult) => {
    if (result.status === 'found') {
      const initial: Record<string, number> = {}
      for (const line of result.lines) {
        if ((line.resolution === 'sku' || line.resolution === 'variant') && line.remaining > 0)
          initial[line.lineKey] = 0
      }
      setQuantities(initial)
      info(`Order ${result.orderNumber} loaded`)
      return
    }

    if (result.status === 'multiple_matches') {
      info(result.message || 'Several orders match. Pick one.')
      return
    }

    if (result.status === 'lookup_failed') {
      showError(result.message || 'Lookup failed')
      return
    }

    if (result.status === 'not_returnable' || result.status === 'label_without_order') {
      warning(result.message || 'This order cannot be returned')
      return
    }

    if (result.status === 'label_not_found' || result.status === 'order_not_found') {
      showError(result.message || 'No order or shipment label matches this value')
    }
  }, [info, showError, warning])

  const runLookup = useCallback(async (value: string) => {
    const trimmed = value.trim()
    if (!trimmed) return
    setSuccessState(null)
    setQuantities({})
    setNotes('')
    applyResult(await lookupValue(trimmed))
  }, [lookupValue, applyResult])

  const handlePickCandidate = useCallback(async (candidate: ReturnLookupCandidate) => {
    setQuantities({})
    setScanInput(candidate.orderNumber)
    applyResult(await selectCandidate(candidate))
  }, [selectCandidate, applyResult])

  handleLookupRef.current = (value: string) => {
    void runLookup(value)
  }

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      const target = e.target
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        (target instanceof HTMLElement && target.isContentEditable)
      ) {
        return
      }

      if (scanTimeoutRef.current) clearTimeout(scanTimeoutRef.current)

      const now = Date.now()
      if (e.key === 'Enter') {
        e.preventDefault()
        const currentBuffer = scanBufferRef.current
        if (currentBuffer.length > 0) {
          const timeSinceLastScan = now - lastScanTimeRef.current
          const isDuplicate =
            timeSinceLastScan < 400 && currentBuffer === lastScanValueRef.current
          if (!isDuplicate) {
            lastScanTimeRef.current = now
            lastScanValueRef.current = currentBuffer
            setScanInput(currentBuffer)
            setTimeout(() => handleLookupRef.current(currentBuffer), 50)
          }
          scanBufferRef.current = ''
        }
      } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
        scanBufferRef.current += e.key
        scanTimeoutRef.current = setTimeout(() => {
          scanBufferRef.current = ''
        }, 300)
      }
    }

    window.addEventListener('keypress', handleKeyPress)
    return () => {
      window.removeEventListener('keypress', handleKeyPress)
      if (scanTimeoutRef.current) clearTimeout(scanTimeoutRef.current)
    }
  }, [])

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  const handleSubmitSearch = (e: React.FormEvent) => {
    e.preventDefault()
    void runLookup(scanInput)
  }

  const setLineQty = (lineKey: string, value: number, max: number) => {
    setQuantities((prev) => ({
      ...prev,
      [lineKey]: Math.max(0, Math.min(max, value)),
    }))
  }

  const handleConfirm = async () => {
    if (!user?.id) {
      showError('Your session has expired. Please sign in again.')
      return
    }

    const items = selectableLines
      .filter((line) => (quantities[line.lineKey] || 0) > 0 && line.productId && line.sku)
      .map((line) => ({
        product_id: line.productId!,
        sku: line.sku,
        title: line.title,
        quantity: quantities[line.lineKey],
        unit_price: line.unitPrice,
        image_url: line.imageUrl,
      }))

    if (items.length === 0) {
      warning('Select at least one item quantity to return')
      return
    }

    const result = await confirmReturn({
      userId: user.id,
      items,
      notes: notes.trim() || null,
    })

    if (!result.rpc.success) {
      if (result.rpc.error === 'exceeds_remaining') {
        showError(
          `Cannot return ${result.rpc.requested} of ${result.rpc.sku}: only ${result.rpc.remaining} remaining (${result.rpc.already_returned} already returned of ${result.rpc.sold} sold)`
        )
      } else {
        showError(result.rpc.error || 'Failed to record return')
      }
      return
    }

    success(`Return ${result.rpc.return_number} recorded — ${result.rpc.total_quantity} item(s) restocked`)

    if (result.push) {
      if (result.push.deferredToCron) {
        info('Shopify sync queued and will complete automatically within a few minutes.')
      } else if (!result.push.skipped) {
        if (result.push.failed > 0) {
          warning(
            `${result.push.failed} item(s) did not sync to Shopify and will retry automatically.`
          )
        } else if (result.push.passed > 0) {
          info(`Synced ${result.push.passed} item(s) to the Shopify warehouse location.`)
        }
      }
    }

    setSuccessState({
      returnNumber: result.rpc.return_number || '',
      itemsCount: result.rpc.items_count || items.length,
      totalQuantity: result.rpc.total_quantity || 0,
      push: result.push,
    })
    clearLookup()
    setQuantities({})
    setNotes('')
    setScanInput('')
    inputRef.current?.focus()
  }

  const handleReset = () => {
    clearLookup()
    setQuantities({})
    setNotes('')
    setScanInput('')
    setSuccessState(null)
    inputRef.current?.focus()
  }

  return (
    <div className="min-h-[calc(100dvh-4rem)] flex flex-col bg-slate-50 dark:bg-slate-950">
      <div className="border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-4 sm:px-6 py-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-semibold text-slate-900 dark:text-white flex items-center gap-2">
              <RotateCcw className="h-6 w-6 text-slate-700 dark:text-slate-200" />
              Return Shipment
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              Scan a shipment label barcode or enter an order number to restock returned items to the warehouse.
            </p>
          </div>
          {(lookup || successState) && (
            <Button variant="secondary" size="sm" onClick={handleReset}>
              <X className="h-4 w-4 mr-1" />
              New return
            </Button>
          )}
        </div>

        <form onSubmit={handleSubmitSearch} className="mt-4 flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <ScanBarcode className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              ref={inputRef}
              value={scanInput}
              onChange={(e) => setScanInput(e.target.value)}
              placeholder="Scan label barcode or type order number…"
              className="pl-10"
              autoComplete="off"
              disabled={loading || confirming}
            />
          </div>
          <Button type="submit" loading={loading} disabled={!scanInput.trim() || confirming}>
            <Search className="h-4 w-4 mr-1" />
            Look up
          </Button>
        </form>
      </div>

      <div className="flex-1 overflow-auto px-4 sm:px-6 py-4 pb-28">
        {shopifySync && (
          <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/40 px-4 py-3 text-sm text-blue-800 dark:text-blue-200">
            Syncing to Shopify {shopifySync.done}/{shopifySync.total}…
          </div>
        )}

        {successState && !lookup && (
          <div className="max-w-2xl mx-auto rounded-xl border border-emerald-200 bg-emerald-50 dark:border-emerald-900 dark:bg-emerald-950/30 p-6">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-7 w-7 text-emerald-600 dark:text-emerald-400 shrink-0" />
              <div>
                <h2 className="text-lg font-semibold text-emerald-900 dark:text-emerald-100">
                  Return {successState.returnNumber} recorded
                </h2>
                <p className="text-sm text-emerald-800 dark:text-emerald-200 mt-1">
                  {successState.totalQuantity} unit(s) across {successState.itemsCount} line(s) added back to warehouse stock.
                </p>
                {successState.push?.deferredToCron && (
                  <p className="text-sm text-emerald-700 dark:text-emerald-300 mt-2">
                    Shopify inventory sync is queued and will finish automatically.
                  </p>
                )}
                {successState.push && !successState.push.skipped && !successState.push.deferredToCron && (
                  <p className="text-sm text-emerald-700 dark:text-emerald-300 mt-2">
                    Shopify: {successState.push.passed} synced
                    {successState.push.permanentlySkipped > 0
                      ? `, ${successState.push.permanentlySkipped} skipped`
                      : ''}
                    {successState.push.failed > 0
                      ? `, ${successState.push.failed} pending retry`
                      : ''}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {loading && (
          <div className="flex justify-center py-16">
            <LoadingSpinner text="Looking up order…" />
          </div>
        )}

        {!loading && !lookup && !successState && (
          <EmptyState
            icon={ScanBarcode}
            title="Scan or enter an order"
            description="Use a keyboard wedge scanner on a PostNL label barcode, or type the Shopify order number."
          />
        )}

        {!loading && lookup?.status === 'multiple_matches' && (
          <div className="max-w-2xl mx-auto space-y-3">
            <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
              <h2 className="font-semibold text-slate-900 dark:text-white">
                {lookup.candidates.length} orders match “{lookup.scannedValue}”
              </h2>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                Pick the order the goods came back from.
              </p>
            </div>

            <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 divide-y divide-slate-100 dark:divide-slate-800 overflow-hidden">
              {lookup.candidates.map((candidate) => {
                const notReturnable =
                  (candidate.fulfillmentStatus || '').toUpperCase() === 'UNFULFILLED'

                return (
                  <button
                    key={candidate.shopifyOrderId}
                    type="button"
                    onClick={() => void handlePickCandidate(candidate)}
                    disabled={notReturnable || confirming}
                    className="w-full text-left p-4 flex items-center gap-3 hover:bg-slate-50 dark:hover:bg-slate-800/60 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                  >
                    <Hash className="h-4 w-4 text-slate-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 dark:text-white truncate">
                        {candidate.orderNumber}
                      </p>
                      <p className="text-xs text-slate-500 truncate">
                        {candidate.customerName || 'No customer name'}
                        {candidate.orderDate
                          ? ` · ${new Date(candidate.orderDate).toLocaleDateString()}`
                          : ''}
                        {` · ${candidate.itemCount} line(s)`}
                      </p>
                    </div>
                    <span
                      className={`text-xs font-medium px-2 py-0.5 rounded shrink-0 ${
                        notReturnable
                          ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200'
                          : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'
                      }`}
                    >
                      {candidate.fulfillmentStatus || 'No status'}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {!loading && lookup?.status === 'lookup_failed' && (
          <div className="max-w-xl mx-auto rounded-xl border border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/30 p-6 flex gap-3">
            <AlertTriangle className="h-6 w-6 text-red-600 shrink-0" />
            <div>
              <h2 className="font-semibold text-red-900 dark:text-red-100">Lookup failed</h2>
              <p className="text-sm text-red-800 dark:text-red-200 mt-1">
                The order could not be checked, so it is unknown whether it exists. Retry, and if
                this persists report the message below.
              </p>
              <p className="text-xs text-red-700 dark:text-red-300 mt-2 font-mono break-all">
                {lookup.message || 'Unknown error'}
              </p>
            </div>
          </div>
        )}

        {!loading && lookup?.status === 'not_returnable' && (
          <div className="max-w-xl mx-auto rounded-xl border border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30 p-6 flex gap-3">
            <AlertTriangle className="h-6 w-6 text-amber-600 shrink-0" />
            <div>
              <h2 className="font-semibold text-amber-900 dark:text-amber-100">
                Nothing to return
              </h2>
              <p className="text-sm text-amber-800 dark:text-amber-200 mt-1">
                {lookup.message || 'This order was never fulfilled.'}
              </p>
              <p className="text-xs text-amber-700 dark:text-amber-300 mt-2 font-mono">
                {lookup.orderNumber} · {lookup.fulfillmentStatus || 'no status'}
              </p>
            </div>
          </div>
        )}

        {!loading &&
          lookup &&
          (lookup.status === 'label_not_found' ||
            lookup.status === 'order_not_found' ||
            lookup.status === 'label_without_order') && (
            <div className="max-w-xl mx-auto rounded-xl border border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30 p-6 flex gap-3">
              <AlertTriangle className="h-6 w-6 text-amber-600 shrink-0" />
              <div>
                <h2 className="font-semibold text-amber-900 dark:text-amber-100">
                  {lookup.status === 'label_without_order' ? 'Incomplete match' : 'Does not exist'}
                </h2>
                <p className="text-sm text-amber-800 dark:text-amber-200 mt-1">
                  {lookup.message || 'No matching label or order was found.'}
                </p>
                <p className="text-xs text-amber-700 dark:text-amber-300 mt-2 font-mono">
                  Input: {lookup.scannedValue}
                </p>
              </div>
            </div>
          )}

        {!loading && lookup?.status === 'found' && (
          <div className="max-w-4xl mx-auto space-y-4">
            <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 sm:p-5">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="flex items-start gap-2">
                  <Hash className="h-4 w-4 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-xs uppercase tracking-wide text-slate-500">Order</p>
                    <p className="font-semibold text-slate-900 dark:text-white">
                      {lookup.orderNumber}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <User className="h-4 w-4 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-xs uppercase tracking-wide text-slate-500">Customer</p>
                    <p className="font-semibold text-slate-900 dark:text-white">
                      {lookup.customerName || '—'}
                    </p>
                    {lookup.customerEmail && (
                      <p className="text-xs text-slate-500 mt-0.5">{lookup.customerEmail}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Package className="h-4 w-4 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-xs uppercase tracking-wide text-slate-500">Lines</p>
                    <p className="font-semibold text-slate-900 dark:text-white">
                      {lookup.lines.length} sold
                      {blockedLines.length > 0 && (
                        <span className="text-amber-600 dark:text-amber-400 font-normal text-sm">
                          {' '}· {blockedLines.length} blocked
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 divide-y divide-slate-100 dark:divide-slate-800 overflow-hidden">
              {lookup.lines.length === 0 && (
                <div className="p-6 text-sm text-slate-500">This order has no line items.</div>
              )}

              {lookup.lines.map((line) => {
                const canSelect =
                  (line.resolution === 'sku' || line.resolution === 'variant') &&
                  line.remaining > 0
                const isBlocked =
                  line.resolution === 'unresolved' || line.resolution === 'ambiguous'
                const qty = quantities[line.lineKey] || 0

                return (
                  <div
                    key={line.lineKey}
                    className={`p-4 flex flex-col sm:flex-row sm:items-center gap-3 ${
                      isBlocked ? 'opacity-70 bg-slate-50/80 dark:bg-slate-900/50' : ''
                    }`}
                  >
                    <ReturnItemImage src={line.imageUrl} alt={line.title} />
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-medium text-slate-900 dark:text-white truncate">
                          {line.productName || line.title}
                        </p>
                        <ResolutionBadge resolution={line.resolution} />
                      </div>
                      <p className="text-xs text-slate-500 font-mono mt-0.5">
                        {line.sku || 'No SKU'}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        Sold {line.soldQuantity}
                        {line.alreadyReturned > 0 && (
                          <> · Already returned {line.alreadyReturned}</>
                        )}
                        <> · Remaining {line.remaining}</>
                      </p>
                    </div>
                    <div className="shrink-0">
                      {canSelect ? (
                        <QuantityStepper
                          value={qty}
                          onChange={(v) => setLineQty(line.lineKey, v, line.remaining)}
                          min={0}
                          max={line.remaining}
                          size="md"
                          disabled={confirming}
                        />
                      ) : line.remaining <= 0 && !isBlocked ? (
                        <span className="text-xs font-medium text-slate-500">Fully returned</span>
                      ) : (
                        <span className="text-xs font-medium text-slate-500">Not selectable</span>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>

            <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Notes (optional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                disabled={confirming}
                className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-slate-400"
                placeholder="Condition, reason, or receiving notes…"
              />
            </div>
          </div>
        )}
      </div>

      {lookup?.status === 'found' && (
        <div className="fixed bottom-0 inset-x-0 z-30 border-t border-slate-200 dark:border-slate-800 bg-white/95 dark:bg-slate-900/95 backdrop-blur px-4 sm:px-6 py-3 safe-area-pb">
          <div className="max-w-4xl mx-auto flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="text-sm text-slate-600 dark:text-slate-300">
              <span className="font-semibold text-slate-900 dark:text-white">{selectedCount}</span> line(s) ·{' '}
              <span className="font-semibold text-slate-900 dark:text-white">{selectedTotal}</span> unit(s) to restock
            </div>
            <Button
              onClick={() => void handleConfirm()}
              loading={confirming}
              disabled={selectedTotal <= 0 || confirming}
              variant="success"
              size="lg"
            >
              {confirming ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Confirming…
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Confirm return
                </>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
